// ConfirmManualPaymentDialog.tsx
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";
import type { Order } from "../../api/orderApi";
import { fetchStoreCredit } from "../../api/storeCreditApi";
import { useEffect, useState, useRef } from "react";
import { TextField } from "@mui/material";

type ManualMethod = "cash" | "etransfer" | "manual_card" | "other" | "store_credit";

type Props = {
    open: boolean;
    order: Order | null;
    loading: boolean;
    onClose: () => void;
    onConfirm: (payload: {
        amount_collected: number;
        payment_method: ManualMethod;
        note?: string;
    }) => void;
};
export default function ConfirmManualPaymentDialog({
    open,
    order,
    loading,
    onClose,
    onConfirm,
}: Props) {
    const defaultAmount = Number(
        order?.payment_balance?.balanceDue ||
        order?.payments?.balance?.balanceDue ||
        order?.total_price ||
        0
    );

    const [amountCollected, setAmountCollected] = useState(defaultAmount);
    const [paymentMethod, setPaymentMethod] = useState<ManualMethod>("cash");
    const [note, setNote] = useState("");
    // Store-credit state: the customer's account-wide balance (null = not loaded).
    const [storeCreditBalance, setStoreCreditBalance] = useState<number | null>(null);
    const [scLoading, setScLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (open) {
            setAmountCollected(defaultAmount);
            setPaymentMethod(order?.payment_method === "etransfer" ? "etransfer" : "cash");
            setNote("");
            setStoreCreditBalance(null);
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }
    }, [open, defaultAmount]);

    // Load the customer's store credit balance when that method is picked.
    useEffect(() => {
        if (!open || paymentMethod !== "store_credit" || !order?.customer_id) {
            setStoreCreditBalance(null);
            return;
        }
        let active = true;
        setScLoading(true);
        fetchStoreCredit(order.customer_id)
            .then((r) => { if (active) setStoreCreditBalance(r.balance); })
            .catch(() => { if (active) setStoreCreditBalance(0); })
            .finally(() => { if (active) setScLoading(false); });
        return () => { active = false; };
    }, [open, paymentMethod, order?.customer_id]);

    const isStoreCredit = paymentMethod === "store_credit";
    const noCustomer = isStoreCredit && !order?.customer_id;
    // Store credit is capped to the lesser of the balance due and available credit.
    const effectiveMax =
        isStoreCredit && storeCreditBalance != null
            ? Math.min(defaultAmount, storeCreditBalance)
            : defaultAmount;

    // Keep the entered amount within the effective max (e.g. after the balance loads).
    useEffect(() => {
        setAmountCollected((amt) => Math.min(amt, effectiveMax));
    }, [effectiveMax]);

    const insufficientCredit =
        isStoreCredit && storeCreditBalance != null && storeCreditBalance <= 0;
    const confirmDisabled =
        loading ||
        amountCollected <= 0 ||
        noCustomer ||
        insufficientCredit ||
        (isStoreCredit && scLoading);

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Confirm Manual Payment</DialogTitle>

            <DialogContent>
                <Stack spacing={2}>
                    <Alert severity="warning">
                        Only confirm this after the payment has been received.
                    </Alert>

                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Order
                        </Typography>
                        <Typography fontWeight={700}>#{order?.order_number}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Customer
                        </Typography>
                        <Typography>{order?.customer_name || "N/A"}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Record As
                        </Typography>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={paymentMethod}
                            disabled={loading}
                            onChange={(e) => setPaymentMethod(e.target.value as ManualMethod)}
                        >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="etransfer">E-Transfer</MenuItem>
                            <MenuItem value="manual_card">Manual Card</MenuItem>
                            <MenuItem value="store_credit">Store Credit</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>
                    </Box>

                    {isStoreCredit && (
                        noCustomer ? (
                            <Alert severity="error">
                                This order has no customer account, so store credit can't be applied.
                            </Alert>
                        ) : scLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                    Checking store credit balance…
                                </Typography>
                            </Box>
                        ) : storeCreditBalance != null ? (
                            <Alert severity={storeCreditBalance > 0 ? "info" : "warning"}>
                                {storeCreditBalance > 0
                                    ? `Available store credit: $${storeCreditBalance.toFixed(2)} — will be drawn down by the amount below.`
                                    : "This customer has no store credit available."}
                            </Alert>
                        ) : null
                    )}

                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Payment Method
                        </Typography>
                        <Typography>{order?.payment_method?.toUpperCase() || "N/A"}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Amount to Confirm
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={amountCollected === 0 ? "" : amountCollected}
                            inputRef={inputRef}
                            disabled={loading || noCustomer || insufficientCredit}
                            inputProps={{ min: 0, max: effectiveMax, step: "0.01" }}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "") {
                                    setAmountCollected(0);
                                    return;
                                }
                                const value = Number(raw);
                                if (isNaN(value)) return;
                                setAmountCollected(Math.min(value, effectiveMax));
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            Balance due: ${defaultAmount.toFixed(2)}
                            {isStoreCredit && storeCreditBalance != null
                                ? ` · Store credit: $${storeCreditBalance.toFixed(2)}`
                                : ""}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Admin Note (Optional)
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            value={note}
                            disabled={loading}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional internal payment note..."
                        />
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() =>
                        onConfirm({
                            amount_collected: amountCollected,
                            payment_method: paymentMethod,
                            note,
                        })
                    }
                    disabled={confirmDisabled}
                >
                    {loading ? <CircularProgress size={22} /> : "Confirm Payment"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
